import { useCallback, useEffect, useRef, useState, type JSX } from 'react';

import { type ApolloCache } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import classNames from 'classnames';
import { Bell, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '../../../components/Button/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '../../../components/DropdownMenu/DropdownMenu';
import { CenterNotificationRow } from '../../../components/NotificationCenter/CenterNotificationRow';
import {
  EMAIL_SCOPE_HINT,
  EMAIL_SCOPE_OPTIONS,
  EMAIL_SCOPE_TITLE,
  isEmailNotificationScope,
  useEmailNotificationScope,
} from '../../../components/NotificationCenter/emailNotificationScope';
import {
  MARK_NOTIFICATION_CENTER_READ_MUTATION,
  NOTIFICATION_CENTER_QUERY,
  NOTIFICATION_CENTER_UNREAD_COUNT_QUERY,
  type NotificationCenterNode,
} from '../../../components/NotificationCenter/notificationCenterOps';
import { pagingWindow } from '../../../components/NotificationCenter/pagingWindow';
import { Skeleton } from '../../../components/Skeleton/Skeleton';
import { NotificationRowContent } from '../Notifications/NotificationRowContent';
import {
  MARK_SUPPORT_NOTIFICATIONS_READ_MUTATION,
  SUPPORT_NOTIFICATION_UNREAD_COUNT_QUERY,
  SUPPORT_NOTIFICATIONS_QUERY,
  type SupportNotificationNode,
} from '../Notifications/notificationOps';
import { UNREAD_POLL_MS, useNotificationFeed } from '../Notifications/useNotificationFeed';
import { ticketPath } from '../supportFormat';

import styles from './SupportBell.module.css';

export const NOTIFICATIONS_PATH = '/support/notifications';
/** One page of each feed. The panel keeps loading pages as the list is scrolled. */
const PANEL_SIZE = 20;
/** How close to the bottom of the list (px) the next page is requested. */
const LOAD_MORE_DISTANCE = 48;
/** Below the md breakpoint the bell opens the full page instead of a panel. */
const PHONE_QUERY = '(max-width: 767px)';

function isPhone(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(PHONE_QUERY).matches
    : false;
}

function countLabel(count: number): string {
  return count > 99 ? '99+' : String(count);
}

/** A path inside the app. Anything else (a full URL, `//host`) is never navigated to. */
function appPath(link: string | null | undefined): string | null {
  return link && link.startsWith('/') && !link.startsWith('//') ? link : null;
}

type PanelItem =
  | { feed: 'support'; id: string; at: number; node: SupportNotificationNode }
  | { feed: 'center'; id: string; at: number; node: NotificationCenterNode };

const timeOf = (value: unknown): number => new Date(value as string | Date).getTime();

/**
 * The notification bell: unread count, and a panel of the latest 20.
 *
 * One bell, two feeds. The notification center (forms, OKRs, email, CRM
 * meetings, overdue invoices) is available to every workspace; Support ticket
 * notifications are added when `support` is on. Items from both are merged
 * newest first, and each is read and opened through its own feed.
 */
export function SupportBell({ support = true }: { support?: boolean } = {}): JSX.Element {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // The panel shows the list, or the choice of which email rings the bell.
  const [view, setView] = useState<'list' | 'email'>('list');
  const emailScope = useEmailNotificationScope({ skip: !open });
  const emailScopeLabel =
    EMAIL_SCOPE_OPTIONS.find((option) => option.value === emailScope.scope)?.label ?? '';

  // The feed's callbacks run after render, by which point the queries exist.
  const refreshRef = useRef<() => void>(() => undefined);
  const { live } = useNotificationFeed({
    onNotification: () => refreshRef.current(),
    onResync: () => refreshRef.current(),
  });

  const countQuery = useQuery(SUPPORT_NOTIFICATION_UNREAD_COUNT_QUERY, {
    fetchPolicy: 'cache-and-network',
    skip: !support,
    // No stream reaches the bell outside /support/*: keep the count roughly fresh.
    pollInterval: live ? 0 : UNREAD_POLL_MS,
  });
  const listQuery = useQuery(SUPPORT_NOTIFICATIONS_QUERY, {
    variables: { first: PANEL_SIZE },
    fetchPolicy: 'cache-and-network',
    skip: !open || !support,
  });
  const [markRead] = useMutation(MARK_SUPPORT_NOTIFICATIONS_READ_MUTATION);

  // The center has no live stream, so its count is always polled.
  const centerCountQuery = useQuery(NOTIFICATION_CENTER_UNREAD_COUNT_QUERY, {
    fetchPolicy: 'cache-and-network',
    pollInterval: UNREAD_POLL_MS,
  });
  const centerListQuery = useQuery(NOTIFICATION_CENTER_QUERY, {
    variables: { first: PANEL_SIZE },
    fetchPolicy: 'cache-and-network',
    skip: !open,
  });
  const [markCenterRead] = useMutation(MARK_NOTIFICATION_CENTER_READ_MUTATION);

  const refetchCount = countQuery.refetch;
  const refetchList = listQuery.refetch;
  const refetchCenterCount = centerCountQuery.refetch;
  const refetchCenterList = centerListQuery.refetch;
  const refetchAll = (): Promise<unknown> =>
    Promise.all([
      support ? refetchCount() : null,
      support && open ? refetchList() : null,
      refetchCenterCount(),
      open ? refetchCenterList() : null,
    ]);
  refreshRef.current = () => {
    void refetchAll().catch(() => undefined);
  };

  const count =
    (support ? (countQuery.data?.supportNotificationUnreadCount ?? 0) : 0) +
    (centerCountQuery.data?.notificationCenterUnreadCount ?? 0);
  const supportNodes = support ? (listQuery.data?.supportNotifications.nodes ?? []) : [];
  const centerNodes = centerListQuery.data?.notificationCenter.nodes ?? [];
  const supportMore = support && !!listQuery.data?.supportNotifications.pageInfo.hasNextPage;
  const centerMore = !!centerListQuery.data?.notificationCenter.pageInfo.hasNextPage;
  const oldest = (nodes: { createdAt: unknown }[]) =>
    nodes.length ? timeOf(nodes[nodes.length - 1].createdAt) : null;
  // The feeds page independently, so only the part of the merged list that both
  // have reached is shown; the rest waits for the lagging feed to catch up.
  const { cutoff, next: nextFeed } = pagingWindow([
    { key: 'support', oldestAt: oldest(supportNodes), hasMore: supportMore },
    { key: 'center', oldestAt: oldest(centerNodes), hasMore: centerMore },
  ]);
  const items: PanelItem[] = [
    ...supportNodes.map(
      (node): PanelItem => ({ feed: 'support', id: node.id, at: timeOf(node.createdAt), node }),
    ),
    ...centerNodes.map(
      (node): PanelItem => ({ feed: 'center', id: node.id, at: timeOf(node.createdAt), node }),
    ),
  ]
    .filter((item) => cutoff === null || item.at >= cutoff)
    .sort((a, b) => b.at - a.at);
  const hasMore = nextFeed !== null;

  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);
  const listRef = useRef<HTMLDivElement | null>(null);
  const fetchMoreSupport = listQuery.fetchMore;
  const fetchMoreCenter = centerListQuery.fetchMore;
  const supportCursor = listQuery.data?.supportNotifications.pageInfo.endCursor;
  const centerCursor = centerListQuery.data?.notificationCenter.pageInfo.endCursor;

  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current || !nextFeed) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      // Appends to what the query already holds (dropping repeats), so the next
      // page extends the list instead of replacing it.
      if (nextFeed === 'support' && supportCursor) {
        await fetchMoreSupport({
          variables: { first: PANEL_SIZE, after: supportCursor },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prev;
            const have = new Set(prev.supportNotifications.nodes.map((n) => n.id));
            return {
              supportNotifications: {
                ...fetchMoreResult.supportNotifications,
                nodes: [
                  ...prev.supportNotifications.nodes,
                  ...fetchMoreResult.supportNotifications.nodes.filter((n) => !have.has(n.id)),
                ],
              },
            };
          },
        });
      } else if (nextFeed === 'center' && centerCursor) {
        await fetchMoreCenter({
          variables: { first: PANEL_SIZE, after: centerCursor },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prev;
            const have = new Set(prev.notificationCenter.nodes.map((n) => n.id));
            return {
              notificationCenter: {
                ...fetchMoreResult.notificationCenter,
                nodes: [
                  ...prev.notificationCenter.nodes,
                  ...fetchMoreResult.notificationCenter.nodes.filter((n) => !have.has(n.id)),
                ],
              },
            };
          },
        });
      }
    } catch {
      // The next scroll tries again.
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [nextFeed, supportCursor, centerCursor, fetchMoreSupport, fetchMoreCenter]);

  const onListScroll = () => {
    const el = listRef.current;
    if (el && el.scrollHeight - el.scrollTop - el.clientHeight < LOAD_MORE_DISTANCE) {
      void loadMore();
    }
  };

  // A page can leave the list shorter than the panel (the hidden part waits on
  // the other feed), in which case there is nothing to scroll and no scroll
  // event to ask for more. Keep loading until it overflows or runs out.
  useEffect(() => {
    const el = listRef.current;
    if (open && hasMore && el && el.clientHeight > 0 && el.scrollHeight <= el.clientHeight) {
      void loadMore();
    }
  }, [open, hasMore, items.length, loadMore]);
  const loading = listQuery.loading || centerListQuery.loading;
  const failed = !!listQuery.error || !!centerListQuery.error;

  const markOneRead = (item: PanelItem) => {
    if (item.node.readAt) return;
    const typename = item.feed === 'support' ? 'SupportNotification' : 'NotificationCenterItem';
    const update = (cache: ApolloCache) => {
      cache.modify({
        id: cache.identify({ __typename: typename, id: item.id }),
        fields: { readAt: () => new Date().toISOString() },
      });
    };
    const done: Promise<unknown> =
      item.feed === 'support'
        ? markRead({ variables: { ids: [item.id] }, update }).then(() => refetchCount())
        : markCenterRead({ variables: { ids: [item.id] }, update }).then(() =>
            refetchCenterCount(),
          );
    void done.catch(() => undefined);
  };

  const markAllRead = () => {
    void Promise.all([
      support ? markRead({ variables: {} }) : null,
      markCenterRead({ variables: {} }),
    ])
      .then(() => refetchAll())
      .catch(() => undefined);
  };

  const openItem = (item: PanelItem) => {
    markOneRead(item);
    if (item.feed === 'support') {
      navigate(ticketPath(item.node.ticketNumber));
      return;
    }
    const to = appPath(item.node.link);
    if (to) navigate(to);
  };

  const onOpenChange = (next: boolean) => {
    if (next && isPhone()) {
      navigate(NOTIFICATIONS_PATH);
      return;
    }
    // Opening again starts from the list, not from where it was left.
    if (!next) setView('list');
    setOpen(next);
  };

  const label = `Notifications, ${count} unread`;

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange} modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          forIconOnly
          aria-label={label}
          className={styles.Bell}
        >
          <Bell aria-hidden="true" />
          {count > 0 && (
            <span className={styles.Count} aria-hidden="true">
              {countLabel(count)}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className={styles.Panel}
        aria-label="Notifications"
      >
        {view === 'email' ? (
          <>
            <div className={classNames(styles.Header, styles.HeaderSettings)}>
              <DropdownMenuItem
                className={styles.Back}
                aria-label="Back to notifications"
                onSelect={(event) => {
                  event.preventDefault();
                  setView('list');
                }}
              >
                <ChevronLeft aria-hidden="true" />
              </DropdownMenuItem>
              <span className={styles.HeaderTitle}>{EMAIL_SCOPE_TITLE}</span>
            </div>
            <div className={styles.Settings}>
              <p className={styles.SettingsHint}>{EMAIL_SCOPE_HINT}</p>
              {emailScope.failed ? (
                <p className={styles.Message} role="status">
                  Your choice could not be loaded.
                </p>
              ) : (
                <DropdownMenuRadioGroup
                  value={emailScope.scope}
                  onValueChange={(value) => {
                    if (isEmailNotificationScope(value) && value !== emailScope.scope) {
                      emailScope.setScope(value);
                    }
                  }}
                >
                  {EMAIL_SCOPE_OPTIONS.map((option) => (
                    <DropdownMenuRadioItem
                      key={option.value}
                      value={option.value}
                      indicator="check"
                      disabled={!emailScope.loaded}
                      className={styles.Choice}
                      // Picking keeps the panel open, so the choice is seen to stick.
                      onSelect={(event) => event.preventDefault()}
                    >
                      {option.label}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              )}
            </div>
          </>
        ) : (
          <>
            <div className={styles.Header}>
              <span className={styles.HeaderTitle}>Notifications</span>
              <DropdownMenuItem
                className={styles.MarkAll}
                disabled={count === 0}
                onSelect={(event) => {
                  event.preventDefault();
                  markAllRead();
                }}
              >
                Mark all read
              </DropdownMenuItem>
            </div>

            <div className={styles.List} ref={listRef} onScroll={onListScroll}>
              {loading && items.length === 0 ? (
                <div className={styles.Loading} aria-busy="true">
                  <Skeleton height="2.5rem" />
                  <Skeleton height="2.5rem" />
                  <Skeleton height="2.5rem" />
                </div>
              ) : failed && items.length === 0 ? (
                <p className={styles.Message} role="status">
                  Notifications could not be loaded.{' '}
                  <Button variant="ghost" size="xs" onClick={() => void refetchAll()}>
                    Try again
                  </Button>
                </p>
              ) : items.length === 0 && !hasMore ? (
                <p className={styles.Message} role="status">
                  No notifications yet.
                </p>
              ) : (
                items.map((item) => (
                  <DropdownMenuItem
                    key={`${item.feed}:${item.id}`}
                    className={item.node.readAt ? styles.Item : styles.ItemUnread}
                    onSelect={() => openItem(item)}
                  >
                    {item.feed === 'support' ? (
                      <NotificationRowContent notification={item.node} />
                    ) : (
                      <CenterNotificationRow notification={item.node} />
                    )}
                  </DropdownMenuItem>
                ))
              )}
              {loadingMore && items.length > 0 && (
                <p className={styles.Message} role="status" aria-live="polite">
                  Loading more…
                </p>
              )}
            </div>

            <DropdownMenuItem
              className={styles.EmailScope}
              onSelect={(event) => {
                event.preventDefault();
                setView('email');
              }}
            >
              <span className={styles.EmailScopeLabel}>{EMAIL_SCOPE_TITLE}</span>
              <span className={styles.EmailScopeValue}>
                {emailScope.loaded ? emailScopeLabel : ''}
                <ChevronRight aria-hidden="true" />
              </span>
            </DropdownMenuItem>

            {support && (
              <DropdownMenuItem asChild className={styles.Footer}>
                <Link to={NOTIFICATIONS_PATH}>Notification preferences</Link>
              </DropdownMenuItem>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
